import {
	SCHEMA_ANY_COMP_PROP,
	SCHEMA_BOOL_COMP_PROP,
	SCHEMA_NUM_COMP_PROP,
	SCHEMA_STRING_COMP_PROP,
} from '../../constants';
import {
	ComponentPropertyDefinition,
	ComponentPropertyEditor,
	ComponentPropertyGroup,
	ComponentStylePropertyDefinition,
} from '../../types/common';
import { COMPONENT_STYLE_GROUP_PROPERTIES } from '../util/properties';

const propertiesDefinition: Array<ComponentPropertyDefinition> = [
	{
		name: 'theme',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Theme',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		description: 'Editor color schemes',
		defaultValue: '_light',
		enumValues: [
			{
				name: '_light',
				displayName: 'Light Theme',
				description: 'Light colors look and feel',
			},
			{
				name: '_dark',
				displayName: 'Dark Theme',
				description: 'Dark colors look and feel',
			},
		],
	},

	{
		name: 'logo',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'SVG logo',
		group: ComponentPropertyGroup.BASIC,
		description: 'SVG Logo with no fill to match the theme',
	},

	{
		name: 'onSave',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Save',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on page save.',
	},

	{
		name: 'onPublish',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Publish',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on page publish.',
	},

	{
		// Publishing means "make the draft live", so with no draft there is
		// nothing the button can do: it answered 404 and the only way to find
		// that out was to press it. The host owns the answer because the host
		// owns the read that carries `X-Draft-Version`.
		//
		// Undefined, not false, is the no-answer case: a host that wires
		// onPublish and never sets this keeps the button it has always had.
		// Only an explicit false hides it.
		name: 'hasDraft',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Draft Exists',
		group: ComponentPropertyGroup.ADVANCED,
		description:
			'Whether this page has unpublished work. False hides the Publish button; leave unset to always show it.',
	},

	{
		name: 'pagesData',
		schema: SCHEMA_ANY_COMP_PROP,
		displayName: 'Pages Data',
		group: ComponentPropertyGroup.DATA,
		description: 'Data of pages list.',
	},

	{
		name: 'currentPageId',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Current Page ID',
		group: ComponentPropertyGroup.DATA,
		description: 'Current Page ID.',
	},

	{
		name: 'dashboardPageMenuName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Dashboard Menu Name',
		group: ComponentPropertyGroup.DATA,
		description: 'Dashboard menu name.',
		defaultValue: 'View Dashboard',
	},

	{
		name: 'dashboardPageName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Dashboard page',
		group: ComponentPropertyGroup.DATA,
		description: 'Dashboard page.',
	},

	{
		name: 'pagesPageMenuName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Pages Menu Name',
		group: ComponentPropertyGroup.DATA,
		description: 'Pages menu name.',
		defaultValue: 'Pages',
	},

	{
		name: 'pagesPageUrl',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Pages page URL',
		group: ComponentPropertyGroup.DATA,
		description: 'URL to navigate to the pages list.',
	},

	{
		name: 'formStorageUrl',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Url of Form storage',
		group: ComponentPropertyGroup.DATA,
		description: 'Url of Form storage. ',
	},

	{
		name: 'settingsPageMenuName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Site settings menu name',
		group: ComponentPropertyGroup.DATA,
		description: 'Site settings menu name.',
		defaultValue: 'Settings',
	},

	{
		name: 'settingsPageName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Site settings page',
		group: ComponentPropertyGroup.DATA,
		description: 'Site settings page.',
	},

	{
		name: 'addnewPageName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Name of add new page page',
		group: ComponentPropertyGroup.DATA,
		description: 'Name of add new page page.',
	},

	{
		name: 'onVersions',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Published Versions',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on page published versions request.',
	},

	{
		name: 'onSavedVersions',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Published Versions',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on page saved versions request.',
	},

	{
		name: 'onChangePersonalization',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Personalization Change',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on personalization changed.',
	},

	{
		name: 'onDeletePersonalization',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On Personalization Delete',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		group: ComponentPropertyGroup.EVENTS,
		description: 'Event to be triggered on personalization cleared.',
	},

	{
		name: 'editorType',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Editor Type',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		description: 'Editor type',
		defaultValue: 'PAGE',
		enumValues: [
			{
				name: 'PAGE',
				displayName: 'Page Editor',
				description: 'Page Editor',
			},
			{
				name: 'SECTION',
				displayName: 'Section Editor',
				description: 'Section Editor',
			},
		],
	},

	{
		name: 'sectionsCategoryList',
		schema: SCHEMA_ANY_COMP_PROP,
		displayName: 'Sections Category List',
		group: ComponentPropertyGroup.DATA,
	},

	{
		name: 'sectionsListConnectionName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Section Items Connection Name',
		group: ComponentPropertyGroup.DATA,
	},

	{
		name: 'helpURL',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Help URL',
		group: ComponentPropertyGroup.ADVANCED,
		description: 'URL to the help page.',
	},

	{
		name: 'defaultZoomPercentage',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Default Zoom Percentage',
		group: ComponentPropertyGroup.BASIC,
		description: 'Default zoom percentage for the editor canvas (e.g. 100 for 100%).',
		defaultValue: 100,
	},
	{
		// Off unless a host asks for it. The panel talks to an AI service that only
		// the appbuilder deployment runs, so defaulting it on would put a dead chat
		// in every other embedder's editor.
		name: 'sidekickEnabled',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Enable AI Sidekick',
		group: ComponentPropertyGroup.BASIC,
		description:
			'Show the docked AI panel. Its edits to this page land on the canvas unsaved, for review before Save; anything else it touches is saved as usual.',
		defaultValue: false,
	},
	{
		// Drafting by default, because the editor itself now saves and loads the
		// draft surface: an agent still writing live would be editing a different
		// copy of the page than the one on the canvas. The agent probes the
		// deployment and falls back to live writes where there is no draft
		// surface, so this is safe to leave on.
		//
		// An editor that publishes one page at a time wants 'PAGE_ONLY_DRAFT': it
		// has a review step for the page, and none for a storage or a connection,
		// so drafting one of those would strand it unpublished.
		name: 'sidekickDraftMode',
		schema: SCHEMA_STRING_COMP_PROP,
		editor: ComponentPropertyEditor.ENUM,
		displayName: 'Where AI Edits Land',
		group: ComponentPropertyGroup.ADVANCED,
		description:
			"Which of the AI panel's edits go to the app's draft surface, matching where the editor itself saves.",
		defaultValue: 'DRAFT',
		enumValues: [
			{
				name: 'DRAFT',
				displayName: 'Draft Everything',
				description:
					'Every definition edit waits on the draft surface until someone publishes it.',
			},
			{
				name: 'PAGE_ONLY_DRAFT',
				displayName: 'Draft Pages Only',
				description:
					'Pages, styles and themes wait for review. Storages, connections, schemas and functions go live immediately.',
			},
			{
				name: 'LIVE',
				displayName: 'Write Live',
				description: 'Every edit goes straight to the live app, with no review step.',
			},
		],
	},
	{
		name: 'sidekickAgentEndpoint',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'AI Sidekick Endpoint',
		group: ComponentPropertyGroup.ADVANCED,
		description: 'SSE endpoint the docked AI panel talks to.',
		defaultValue: '/api/ai/appbuilder/chat',
	},
	{
		// A property rather than a constant inside the panel, for the same reason
		// the endpoint above is one: this component is embeddable by any app, and
		// the name of a page in the appbuilder app is not something it should know.
		// Empty by default, which is what hides the icon.
		name: 'sidekickOpenFullPageName',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'AI Sidekick Full Page',
		group: ComponentPropertyGroup.ADVANCED,
		description:
			'Page the docked AI panel offers to continue the conversation in, carrying the session in the URL. Leave empty to offer nothing.',
	},
];

const stylePropertiesDefinition: ComponentStylePropertyDefinition = {
	'': [
		COMPONENT_STYLE_GROUP_PROPERTIES.layout.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.position.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.border.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.background.type,
	],
};

export { propertiesDefinition, stylePropertiesDefinition };
